import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import logger from "../../lib/logger";

type ExtendedWindow = Window &
  typeof globalThis & {
    fully: {
      bind: (event: string, handler: string) => void;
      nfcScanStart: (flags: number, debounceMs: number) => boolean;
      nfcScanStop: () => void;
    };
    onNdefDiscovered: (serial: string, message: string, data: string) => void;
    onNfcTagDiscovered: (serial: string, type: string, message: string, data: string) => void;
    onNfcTagRemoved: (serial: string) => void;
  };

// const isDev = process.env.NODE_ENV === "development";
// const CLIENT_TOKEN = isDev
//   ? process.env.API_CLIENT_TOKEN
//   : window.location.pathname.split("/")[2];
// const BOARD_ID = isDev
//   ? process.env.LOCALE_BOARD_ID
//   : window.location.pathname?.split?.("/")?.[4] ?? null;

interface ProdvxNfcContextValue { }

const PRODVX_UA_STRING = 'Linux; Android 13; APPC';
const NfcContext = React.createContext<ProdvxNfcContextValue>(
  null!,
);

export const useNfc = () => useContext(NfcContext);

interface ProdvxNfcProviderProps {
  children: React.ReactNode;
}

export const ProdvxNfcProvider = React.memo<ProdvxNfcProviderProps>(
  (props) => {
    const navigate = useNavigate();

    const { children } = props;

    // const onNdefDiscovered = (serial: string, message: string, data: string) => {
    // };

    const onNfcTagDiscovered = useCallback((serial: string, type: string, message: string, data: string) => {
      let code;

      try {
        const [record] = JSON.parse(message);
        const languageCodeLength = parseInt(record.payload.substr(0, 2), 16);
        code = Buffer.from(record.payload.substr(2 + languageCodeLength * 2), 'hex').toString('utf8');
      } catch (e) {
        logger.error(`[nfc] Failed to JSON parse NDEF message: ${e}`);
        // fallback serial or message
        code = serial || message;
      }
      console.log(code);
      logger.debug(`[nfc] Tag detected, serial: ${serial}`);
      navigate(`/scan/${code}?isNFC=true`);
    }, [navigate]);


    /**
    Additional methods that can be bound to window.fully:
      - onNdefDiscovered - binds for NDEF discovered event
      - onNfcTagRemoved - binds for NFC tag removed event

    Here are examples of how to bind these methods if needed in the future:
      ```js
      const onNdefDiscovered = (serial: string, message: string, data: string) => {};

      const onNfcTagRemoved = (serial: string) => {};

      extendedWindow.onNdefDiscovered = onNdefDiscovered;
      Bind handlers to Fully events
      try {
        extendedWindow.fully.bind('onNdefDiscovered', 'onNdefDiscovered("$serial","$message","$data");');
      } catch (error) {}

      extendedWindow.onNfcTagRemoved = onNfcTagRemoved;
      try {
        extendedWindow.fully.bind('onNfcTagRemoved', 'onNfcTagRemoved("$serial");');
      } catch (error) {}
      ```

    */
    const bindNfcMethods = useCallback(() => {
      const extendedWindow = window as ExtendedWindow;

      if (!extendedWindow.fully) {
        logger.warn("[nfc] window.fully is not available");
        return;
      }

      // Bind the onNfcTagDiscovered handler
      extendedWindow.onNfcTagDiscovered = onNfcTagDiscovered;
      try {
        extendedWindow.fully.bind(
          "onNfcTagDiscovered",
          'onNfcTagDiscovered("$serial","$type","$message","$data");'
        );
      } catch (error) {
        logger.debug(`[nfc] Failed to bind onNfcTagDiscovered event: ${error}`);
      }

      // Start NFC scanning (1 = default flags, 500ms debounce)
      const ok = extendedWindow.fully.nfcScanStart(1, 500);

      if (!ok) {
        logger.error("[nfc] Failed to start NFC scanning");
      }
    }, [onNfcTagDiscovered]);

    useEffect(() => {
      bindNfcMethods();

      // Stop scan when leaving the page (optional cleanup)
      const handleBeforeUnload = () => {
        try {
          const extendedWindow = window as ExtendedWindow;
          extendedWindow.fully.nfcScanStop();
        } catch (e) {
          logger.debug(`[nfc] Failed to stop NFC scanning: ${e}`);
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, []);

    const value = useMemo(
      () => ({
        onNfcTagDiscovered,
      }),
      [
        onNfcTagDiscovered,
      ],
    );

    return (
      <NfcContext.Provider value={value}>
        {children}
      </NfcContext.Provider>
    );
  },
);

export default ProdvxNfcProvider;
